<template>
  <WidgetWrapper v-bind="props" :style="{ width: '100%' }">
    <div class="map-attribution" role="region" :aria-label="$t('views.map.attribution.label')" data-testid="map-attribution">
      <!-- Only a button when there is something to expand, as in the native ArcGIS widget -->
      <component
        :is="interactive ? 'button' : 'div'"
        v-if="sourcesText"
        ref="sourcesEl"
        class="map-attribution__sources"
        :class="{ 'map-attribution__sources--open': expanded }"
        :type="interactive ? 'button' : undefined"
        :title="overflowed ? sourcesText : undefined"
        :aria-expanded="interactive ? expanded : undefined"
        data-testid="map-attribution-sources"
        @click="interactive && (expanded = !expanded)">
        {{ sourcesText }}
      </component>
      <div class="map-attribution__powered-by">
        {{ $t('views.map.attribution.poweredBy') }}
        <a href="https://www.esri.com/" target="_blank" rel="noreferrer">Esri</a>
      </div>
    </div>
  </WidgetWrapper>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import { useResizeObserver } from '@vueuse/core'

import BasicWidgetProps from '@tsyl/karonte/lib/widgets/BasicWidgetProps'
import { WidgetWrapper } from '@tsyl/karonte-widgets'

import useMapAttribution from '@/composables/useMapAttribution'

const props = defineProps({
  ...BasicWidgetProps,
  // WidgetWrapper orders widgets by index: stay after any other widget sharing the container
  // (e.g. KTimePlayer on narrow screens)
  index: { type: Number, default: 999 },
  // Full-width bar flush with the map edge
  padding: { type: String, default: '0' },
  /** Id of the karonte map to attribute; defaults to the map this component is rendered in. */
  idMap: { type: String, default: undefined }
})

const { sourcesText } = useMapAttribution(props.idMap)

const sourcesEl = ref<HTMLElement>()
const expanded = ref(false)
const overflowed = ref(false)
const interactive = computed(() => expanded.value || overflowed.value)

function checkOverflow() {
  // While expanded the text wraps, so keep the last collapsed measurement
  if (expanded.value || !sourcesEl.value) return
  overflowed.value = sourcesEl.value.scrollWidth > sourcesEl.value.clientWidth
}

useResizeObserver(sourcesEl, checkOverflow)
watch([sourcesText, expanded], () => nextTick(checkOverflow))
</script>

<style scoped lang="scss">
.map-attribution {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
  // karonte's widget grid sizes its tracks to content: without this, long sources widen the whole map
  // container instead of being truncated
  contain: inline-size;
  padding: 2px 8px;
  font-size: 12px;
  line-height: 16px;
  color: rgb(var(--v-theme-on-surface));
  background: rgba(var(--v-theme-surface), 0.8);
}

.map-attribution__sources {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  // Reset native button styles so both variants look the same
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
}

button.map-attribution__sources {
  cursor: pointer;
}

.map-attribution__sources--open {
  white-space: normal;
}

.map-attribution__powered-by {
  margin-left: auto;
  flex: 0 0 auto;
  white-space: nowrap;

  a {
    color: inherit;
  }
}
</style>
